import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import MathText from '../components/MathText';

export default function Results() {
  const { gameId } = useParams();
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [results, setResults] = useState(null);

  useEffect(() => {
    const raw = sessionStorage.getItem(`results_${gameId}`);
    if (raw) {
      try {
        setResults(JSON.parse(raw));
        refreshUser();
      } catch {}
    } else {
      navigate('/');
    }
  }, [gameId, navigate, refreshUser]);

  if (!results) {
    return (
      <div className="page" style={{ textAlign: 'center', paddingTop: 80 }}>
        <div className="queue-spinner" style={{ margin: '0 auto' }} />
        <p style={{ marginTop: 16, color: 'var(--cb-gray-500)' }}>Loading results…</p>
      </div>
    );
  }

  const { isWinner, questions } = results;
  const isDraw = !results.winner;
  const isP1 = results.player1.userId === user?.id;
  const me = isP1 ? results.player1 : results.player2;
  const myAnswers = me.answers || {};

  return (
    <div className="results-page">
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        <h2 style={{ fontSize: 22, fontWeight: 800, color: 'var(--cb-navy-text)', margin: 0 }}>
          Answer Review
        </h2>
        <Link to="/" className="btn btn-outline btn-sm">← Home</Link>
      </div>

      {/* Per-section accuracy */}
      {questions && questions.length >= 12 && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 24 }}>
          {[
            { label: 'Reading & Writing', range: [0, 6] },
            { label: 'Math', range: [6, 12] },
          ].map(({ label, range }) => {
            const sectionQs = questions.slice(...range);
            const half = range[1] - range[0];
            const correct = sectionQs.filter((q, idx) => myAnswers[range[0] + idx] === q.correctAnswer).length;
            const pct = Math.round((correct / half) * 100);
            return (
              <div key={label} style={{ background: 'var(--cb-gray-100)', borderRadius: 10, padding: '14px 16px' }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--cb-gray-700)', marginBottom: 6 }}>{label}</div>
                <div style={{ fontSize: 24, fontWeight: 800, color: 'var(--cb-navy-text)' }}>{correct}/{half}</div>
                <div style={{ height: 4, background: 'var(--cb-gray-300)', borderRadius: 2, marginTop: 8, overflow: 'hidden' }}>
                  <div style={{
                    height: '100%', width: `${pct}%`,
                    background: pct >= 70 ? 'var(--cb-green)' : pct >= 50 ? 'var(--cb-yellow)' : 'var(--cb-red)',
                    borderRadius: 2, transition: 'width 1s ease',
                  }} />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Question-by-question breakdown */}
      {questions && questions.length > 0 && (
        <div className="results-review">
          <h3>Questions</h3>
          {questions.map((q, i) => {
            const myAns = myAnswers[i];
            const isCorrect = myAns === q.correctAnswer;
            const skipped = myAns === undefined;
            return (
              <div key={i} className={`review-item ${skipped ? '' : isCorrect ? 'correct-item' : 'incorrect-item'}`}>
                <div className="review-qnum">
                  Q{i + 1} · {q.section} · {q.skill || q.domain}
                </div>
                {q.figure && (
                  <img
                    src={`/figures/${q.figure}`}
                    alt="Question figure"
                    style={{ maxWidth: '100%', margin: '6px 0', borderRadius: 6, border: '1px solid var(--cb-gray-200)', display: 'block' }}
                  />
                )}
                <div className="review-question"><MathText text={q.question} /></div>
                <div className="review-answers">
                  {skipped ? (
                    <span className="review-tag skipped">Skipped</span>
                  ) : (
                    <>
                      <span className={`review-tag ${isCorrect ? 'correct' : 'incorrect'}`}>
                        {isCorrect ? 'Correct' : 'Incorrect'} — {myAns}) <MathText text={q.choices?.[myAns]} />
                      </span>
                      {!isCorrect && (
                        <span className="review-tag correct">
                          Answer: {q.correctAnswer}) <MathText text={q.choices?.[q.correctAnswer]} />
                        </span>
                      )}
                    </>
                  )}
                </div>
                {!isCorrect && q.explanation && (
                  <div style={{ marginTop: 6, fontSize: 12, color: 'var(--cb-gray-500)', borderTop: '1px solid var(--cb-gray-100)', paddingTop: 6 }}>
                    {q.explanation}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'center', marginTop: 32 }}>
        <Link to="/" className="btn btn-outline">← Home</Link>
      </div>
      <div style={{ height: 48 }} />
    </div>
  );
}
